/**
 * Safe diagnostics for Edge speech and the browser's Realtime handshake.
 * Never retain provider messages/bodies, prompts, credentials, or SDP.
 */
export type OpenAiFailureReason =
  | "rate_limited" | "capacity_limit" | "overloaded" | "unknown_limit" | "http_error";

const CAPACITY_CODES = new Set([
  "insufficient_quota", "credit_balance_exhausted", "billing_limit_exceeded",
  "organization_usage_limit_exceeded", "billing_hard_limit_reached", "usage_limit_exceeded",
]);
const SAFE_CODES = new Set([
  ...CAPACITY_CODES, "rate_limit_exceeded", "slow_down", "server_is_overloaded",
  "invalid_api_key", "model_not_found",
]);
const SAFE_TYPES = new Set([
  "insufficient_quota", "rate_limit_error", "service_unavailable_error",
  "server_error", "invalid_request_error", "authentication_error",
]);
const ERROR_BODY_BYTES = 8192;

export function retryAfterSeconds(value: string | null, now = Date.now()): number | null {
  if (!value?.trim()) return null;
  const raw = value.trim();
  const seconds = /^\d+(?:\.\d+)?$/.test(raw)
    ? Number(raw) : (Date.parse(raw) - now) / 1000;
  return Number.isFinite(seconds) && seconds >= 0 && seconds <= Number.MAX_SAFE_INTEGER
    ? Math.ceil(seconds) : null;
}

export class OpenAiHttpError extends Error {
  readonly reason: OpenAiFailureReason;
  constructor(
    readonly operation: "speech" | "realtime",
    readonly status: number,
    readonly code: string | null,
    readonly type: string | null,
    readonly requestId: string | null,
    readonly retryAfter: number | null,
  ) {
    const capacity = CAPACITY_CODES.has(code ?? "") || type === "insufficient_quota";
    const reason: OpenAiFailureReason = capacity ? "capacity_limit"
      : status === 429 && (code === "rate_limit_exceeded" || code === "slow_down" || type === "rate_limit_error")
        ? "rate_limited"
      : status === 503 && code === "server_is_overloaded" ? "overloaded"
      : status === 429 ? "unknown_limit" : "http_error";
    super("OpenAI " + operation + " HTTP " + status + " [" + reason + "]");
    this.name = "OpenAiHttpError";
    this.reason = reason;
  }

  diagnostics() {
    return {
      provider: "openai", operation: this.operation, upstream_status: this.status,
      provider_code: this.code, provider_type: this.type, provider_request_id: this.requestId,
      provider_reason: this.reason, retry_after_seconds: this.retryAfter,
    };
  }
}

/** Bound bytes and reading time; malformed/HTML/oversized responses stay unclassified. */
export async function readOpenAiHttpError(
  response: Response, operation: "speech" | "realtime",
): Promise<OpenAiHttpError> {
  let json: unknown = null;
  const reader = response.body?.getReader();
  if (reader) {
    const chunks: Uint8Array[] = [];
    let size = 0, complete = false, timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      void reader.cancel().catch(() => undefined);
    }, 1500);
    try {
      while (!timedOut) {
        const next = await reader.read();
        if (next.done) { complete = true; break; }
        size += next.value.byteLength;
        if (size > ERROR_BODY_BYTES) break;
        chunks.push(next.value);
      }
      if (complete && !timedOut && size <= ERROR_BODY_BYTES) {
        const bytes = new Uint8Array(size);
        let at = 0;
        for (const chunk of chunks) { bytes.set(chunk, at); at += chunk.byteLength; }
        json = JSON.parse(new TextDecoder().decode(bytes));
      }
    } catch {
      // HTTP status remains actionable when the error body is unreadable.
    } finally {
      clearTimeout(timeout);
      void reader.cancel().catch(() => undefined);
    }
  }
  const error = json && typeof json === "object" && !Array.isArray(json)
    ? (json as { error?: unknown }).error : null;
  const raw = error && typeof error === "object" && !Array.isArray(error)
    ? error as Record<string, unknown> : {};
  const code = typeof raw.code === "string" && SAFE_CODES.has(raw.code) ? raw.code : null;
  const type = typeof raw.type === "string" && SAFE_TYPES.has(raw.type) ? raw.type : null;
  const request = response.headers.get("x-request-id");
  const requestId = request && /^[a-zA-Z0-9_-]{1,120}$/.test(request) ? request : null;
  return new OpenAiHttpError(
    operation, response.status, code, type, requestId,
    retryAfterSeconds(response.headers.get("retry-after")),
  );
}

/** One short pre-output retry only. A long Retry-After is never shortened. */
export function speechRetryDelay(error: OpenAiHttpError, jitter = Math.random()): number | null {
  if (error.reason !== "rate_limited" && error.reason !== "overloaded") return null;
  const delay = error.retryAfter === null ? 500 : error.retryAfter * 1000;
  if (delay > 1750) return null;
  return delay + Math.floor(Math.max(0, Math.min(1, jitter)) * 250);
}
